import { Component, type ReactNode } from "react";

interface State {
  error: Error | null;
}

/**
 * Catches uncaught render errors so a crash in one page doesn't blank the
 * entire shell. Shows a recoverable card; the user can clear it and the
 * UI re-mounts.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-lg rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-6 text-sm">
            <h2 className="mb-2 text-base font-semibold text-red-800 dark:text-red-200">
              Something went wrong
            </h2>
            <p className="text-red-700 dark:text-red-300">
              {this.state.error.message || String(this.state.error)}
            </p>
            <pre className="mt-3 max-h-48 overflow-auto rounded bg-red-100 dark:bg-red-900/50 p-2 text-[11px]">
              {this.state.error.stack}
            </pre>
            <div className="mt-3 flex gap-2">
              <button
                onClick={this.reset}
                className="rounded-md border border-red-400 bg-white dark:bg-red-900/40 px-3 py-1 text-xs"
              >
                Dismiss
              </button>
              <button
                onClick={() => window.location.assign("/")}
                className="rounded-md bg-red-600 px-3 py-1 text-xs text-white"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
